/**
 * Client-side guardrails mirror the backend, which is the source of truth. Both surfaces
 * cap a message at five files of 10MB each; they differ only in which content types they
 * accept, so each passes its own config to the shared composer hook.
 */
export interface ComposerAttachmentConfig {
  /** The `accept` attribute for the file input, so the OS picker filters correctly. */
  accept: string;
  /**
   * Exact content types the backend accepts. Matching the list exactly — rather than
   * accepting any `image/*` — keeps formats the backend rejects, such as the HEIC an
   * iPhone produces by default, from being uploaded only to fail on send.
   */
  acceptedContentTypes: string[];
  /**
   * Extension fallback for files whose browser-reported MIME type is empty, so the
   * guardrail matches the backend's intent rather than rejecting a file it would accept.
   */
  acceptedExtensions: string[];
  errorMessageKeys: {
    invalidType: string;
    tooLarge: string;
    tooMany: string;
  };
  supportsPaste: boolean;
}

export const MAX_ATTACHMENTS_PER_MESSAGE = 5;
export const MAX_ATTACHMENT_SIZE_IN_MB = 10;

const IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png'];
const IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'png'];

export const MESSAGING_ATTACHMENT_CONFIG: ComposerAttachmentConfig = {
  accept: [...IMAGE_CONTENT_TYPES, 'application/pdf'].join(','),
  acceptedContentTypes: [...IMAGE_CONTENT_TYPES, 'application/pdf'],
  acceptedExtensions: [...IMAGE_EXTENSIONS, 'pdf'],
  errorMessageKeys: {
    invalidType: 'attachments.invalid_type_image_or_pdf',
    tooLarge: 'attachments.too_large',
    tooMany: 'attachments.too_many',
  },
  supportsPaste: true,
};

export const TREAD_AGENT_ATTACHMENT_CONFIG: ComposerAttachmentConfig = {
  accept: [...IMAGE_CONTENT_TYPES, 'application/pdf', '.txt', '.csv', '.md'].join(','),
  acceptedContentTypes: [
    ...IMAGE_CONTENT_TYPES,
    'application/pdf',
    'text/plain',
    'text/csv',
    'text/markdown',
  ],
  acceptedExtensions: [...IMAGE_EXTENSIONS, 'pdf', 'txt', 'csv', 'md'],
  errorMessageKeys: {
    invalidType: 'attachments.invalid_type_document',
    tooLarge: 'attachments.too_large',
    tooMany: 'attachments.too_many',
  },
  supportsPaste: false,
};
